/**
 * Application stage/status → PT label + Pill variant — single source of truth.
 *
 * SCREEN_PATTERNS.md §5 (D7): all status text must come from a centralised PT
 * map; nothing raw from the API on screen. These were previously duplicated
 * (and the professional hub rendered raw English `active`/`completed`). The
 * applications list and the professional hub both import from here.
 */
import type { ClientSignal } from '@caliber/shared/dtos'

import type { PillVariant } from '@/components/ui/pill'
import type { ApplicationStatus } from '@/lib/api/applications'

// Onda W5 — `STAGE_META` saiu. Traduzia quatro etapas fixas para pt-BR; as
// etapas passaram a ser as do processo que cada VAGA escolheu, com nomes que o
// próprio utilizador escreve. Não há o que traduzir — e traduzir seria inventar.
// O `StagePill` mostra o nome tal como veio, e tira a cor da POSIÇÃO.

/**
 * O rótulo de EXIBIÇÃO — o que vai dentro de um selo, a nomear a candidatura.
 *
 * "Reprovada"/"Contratada", e não "Rejeitada"/"Contratado": este ficheiro
 * declara-se fonte única desde sempre, mas o quadro e o painel da candidatura
 * tinham cópias locais com a outra grafia, e as três divergiram em silêncio. A
 * grafia que fica é a que dois dos três sítios já usavam, e a única que concorda
 * em género com "candidatura" — o sujeito que estes rótulos qualificam.
 */
export const STATUS_META: Record<ApplicationStatus, { label: string; variant: PillVariant }> = {
  active: { label: 'Ativa', variant: 'info' },
  rejected: { label: 'Reprovada', variant: 'error' },
  // `success` e não `accent`. O `accent` é a cor da MARCA e da AÇÃO, e o Lumen
  // é explícito em que ela nunca marca ESTADO — um desfecho consumado é
  // `--success` (hue 158), não o verde do produto (133). A forma denunciava-o
  // antes da cor: `accent` é a única variante com fundo SÓLIDO
  // (`bg-accent text-accent-fg`), então "Contratada" aparecia como um bloco
  // cheio no meio de pills suaves.
  //
  // Estava assim antes desta alteração; o que mudou é que o cartão do Kanban
  // passou a consumir este mapa, e propagar o desvio para uma quarta superfície
  // seria torná-lo permanente.
  hired: { label: 'Contratada', variant: 'success' },
  withdrawn: { label: 'Retirada', variant: 'warn' },
}

/**
 * O rótulo de ESCOLHA — o que se lê num menu ou num select, a nomear o destino
 * para onde a candidatura vai.
 *
 * Difere do de exibição num ponto e só num: `withdrawn` diz QUEM desistiu. Num
 * selo, "Retirada" chega, porque o contexto está à volta. Numa lista de ações,
 * ao lado de "Reprovada", a mesma palavra sozinha não distingue quem tomou a
 * decisão — e é essa justamente a diferença entre os dois desfechos.
 */
export const STATUS_CHOICE_LABEL: Record<ApplicationStatus, string> = {
  active: 'Ativa',
  rejected: 'Reprovada',
  hired: 'Contratada',
  withdrawn: 'Retirada pelo candidato',
}

/**
 * O que o cliente fez na vitrine (Story 23.21) — o VERBO, sem sujeito, para
 * cada tela o compor: "o cliente marcou interesse" no texto da marca, "O cliente
 * marcou interesse" no Histórico e na aba Vitrine. Três sítios com a mesma
 * frase, e por isso uma fonte só, pela razão do cabeçalho deste ficheiro.
 *
 * Fala do ATO ("marcou interesse"), e nunca do estado ("interessado"): a linha do
 * tempo interna conta o que o cliente FEZ, e cada sinal é um facto novo, não uma
 * correção do anterior. As duas vitrines do cliente (o portal e o catálogo
 * público, desde a Story 23.29) é que mostram o ESTADO, com "Selecionado" e
 * "Descartado" — é outra pergunta, respondida noutro sítio.
 */
export const CLIENT_SIGNAL_VERB: Record<ClientSignal, string> = {
  interested: 'marcou interesse',
  declined: 'descartou',
}

/** Filter chips for the applications list (stage). */
/**
 * Filtros da listagem de candidaturas — por STATUS (onda W5).
 *
 * Era por etapa, e deixou de poder ser: um nome de etapa só significa alguma
 * coisa dentro do processo da vaga dele, e a listagem mistura vagas. Filtrar por
 * etapa é uma pergunta que se faz no QUADRO, onde há uma vaga escolhida.
 */
export const STATUS_FILTERS: { value: 'all' | ApplicationStatus; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Em andamento' },
  { value: 'hired', label: 'Contratadas' },
  { value: 'rejected', label: 'Reprovadas' },
  { value: 'withdrawn', label: 'Retiradas' },
]

type Meta = { label: string; variant: PillVariant }

/**
 * Resolve a stage/status to its meta with a safe fallback (REL-001). The union
 * types guard callers at compile time, but if the API enum ever drifts the raw
 * key must degrade gracefully (a default pill) instead of crashing on
 * `undefined.variant`. Accept `string` so the fallback is reachable at runtime.
 */
export function statusMeta(status: string): Meta {
  return STATUS_META[status as ApplicationStatus] ?? { label: status, variant: 'default' }
}
